import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../firebase";

const AUTH_ERROR_PATTERN = /auth|unauth|permission|sign in/i;

const getAdminIdToken = async (forceRefresh = false) => {
  const user = auth.currentUser;

  if (!user?.getIdToken) {
    return "";
  }

  return user.getIdToken(forceRefresh);
};

/**
 * Calls a document upload admin callable.
 *
 * @param {string} name
 * @param {Object} payload
 * @returns {Promise<Object>}
 */
const callAdminFunction = async (name, payload = {}) => {
  const callable = httpsCallable(functions, name);
  const idToken = payload.idToken || (await getAdminIdToken());
  const callablePayload = idToken ? { ...payload, idToken } : payload;

  try {
    const result = await callable(callablePayload);
    return result.data || {};
  } catch (error) {
    if (auth.currentUser && AUTH_ERROR_PATTERN.test(String(error?.message || error?.code || ""))) {
      const refreshedToken = await getAdminIdToken(true);
      const retryResult = await callable(
        refreshedToken ? { ...payload, idToken: refreshedToken } : payload,
      );
      return retryResult.data || {};
    }

    throw error;
  }
};

/**
 * Loads document upload settings.
 *
 * @returns {Promise<Object>}
 */
export const getDocumentUploadSettings = () =>
  callAdminFunction("getDocumentUploadSettings");

/**
 * Saves document upload settings.
 *
 * @param {Object} settings
 * @returns {Promise<Object>}
 */
export const updateDocumentUploadSettings = (settings) =>
  callAdminFunction("updateDocumentUploadSettings", { settings });

/**
 * Loads provider documents with signed review URLs.
 *
 * @param {Object} filters
 * @returns {Promise<Object>}
 */
export const getProviderDocumentsForReview = (filters = {}) =>
  callAdminFunction("getProviderDocumentsForReview", filters);

/**
 * Reviews one provider document.
 *
 * @param {Object} payload
 * @returns {Promise<Object>}
 */
export const reviewProviderDocument = (payload) =>
  callAdminFunction("reviewProviderDocument", payload);

/**
 * Revokes an upload session.
 *
 * @param {string} sessionId
 * @returns {Promise<Object>}
 */
export const revokeUploadSession = (sessionId) =>
  callAdminFunction("revokeUploadSession", { sessionId });
